import React from "react";
import { ContentItem } from "@/types/content";
import { ContentThumbnail } from "@/components/content/ContentThumbnail";

interface RoomExamCreationGridProps {
  items: ContentItem[];
  selectedIds: string[];
  onToggleItem: (id: string) => void;
}

export const RoomExamCreationGrid: React.FC<RoomExamCreationGridProps> = ({
  items,
  selectedIds,
  onToggleItem,
}) => {
  const isSelected = (id: string) => selectedIds.includes(id);

  return (
    <section className="w-full">
      <div className="mb-10 mt-4 grid h-full w-full grid-cols-1 items-center justify-center gap-4 sm:grid-cols-2 sm:gap-6 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 min-[1920px]:grid-cols-6">
        {items.map((item) => {
          const selected = isSelected(item.id);

          return (
            <div
              key={item.id}
              className="relative w-full"
              onClick={() => onToggleItem(item.id)}
            >
              <article className="group flex cursor-pointer flex-col justify-between rounded-2xl border bg-card shadow-[0_4px_10px_rgba(0,0,0,0.02)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
                <div className="group relative flex flex-col">
                  <div className="absolute right-2.5 top-2.5 z-30 cursor-pointer rounded-full p-1 transition-all duration-200 group-hover:scale-110 group-hover:bg-background/80">
                    <svg
                      className="h-3.5 w-3.5 text-foreground/80"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="1" />
                      <circle cx="12" cy="5" r="1" />
                      <circle cx="12" cy="19" r="1" />
                    </svg>
                  </div>

                  <div className="relative overflow-hidden rounded-t-2xl border-b">
                    <ContentThumbnail
                      type={item.type}
                      thumbnailUrl={item.thumbnailUrl}
                      title={item.title}
                    />
                  </div>

                  <div className="relative my-2.5 flex w-full items-center gap-2 px-3 py-1">
                    <ContentTypeIcon type={item.type} />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-col gap-1">
                        <div className="flex w-full items-center gap-2">
                          <h5
                            aria-label="title"
                            className="flex-1 truncate text-sm font-medium tracking-wide text-primary/80 group-hover:text-primary"
                          >
                            {item.title}
                          </h5>
                          <svg
                            className="h-4 w-4 flex-shrink-0 cursor-pointer text-primary/40 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
                            <path d="m15 5 4 4" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </article>

              {selected && (
                <div className="pointer-events-none absolute inset-0 z-30 rounded-2xl border-2 border-primary/70 bg-muted/60">
                  <div className="flex h-full w-full items-center justify-center">
                    <svg
                      className="h-8 w-8 stroke-[3] text-primary/80"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

const ContentTypeIcon: React.FC<{ type: ContentItem["type"] }> = ({ type }) => {
  if (type === "audio") {
    return (
      <svg
        className="mr-1 h-4 w-4 flex-shrink-0 text-muted-foreground"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M2 10v3" />
        <path d="M6 6v11" />
        <path d="M10 3v18" />
        <path d="M14 8v7" />
        <path d="M18 5v13" />
        <path d="M22 10v3" />
      </svg>
    );
  }

  if (type === "video") {
    return (
      <svg
        className="mr-1 h-4 w-4 flex-shrink-0 text-muted-foreground"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M5 5a2 2 0 0 1 3.008-1.728l11.997 6.998a2 2 0 0 1 .003 3.458l-12 7A2 2 0 0 1 5 19z" />
      </svg>
    );
  }

  return (
    <svg
      className="mr-1 h-4 w-4 flex-shrink-0 text-muted-foreground"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 5H3" />
      <path d="M15 12H3" />
      <path d="M17 19H3" />
    </svg>
  );
};
